import { useSelector } from '@/api/state';
import { useRegion } from '@/api/rest/users/useRegion';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { Button, TDropdownItem } from '@payaca/ambrosia';
import {
  useChangeProposal,
  useCustomerForDeal,
  useDeal,
  useUploads,
} from '@payaca/store/hooks/appState';
import * as uploadsActions from '@payaca/store/uploads/uploadsActions';
import { backOff } from 'exponential-backoff';
import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import BounceResendModal from '@/ui/components/bounceResendModal/BounceResendModal';
import ChangeProposalDocument from '@/ui/components/changeProposalDocument/ChangeProposalDocument';
import ClientPortalPreviewButton from '@/ui/components/clientPortalPreview/ClientPortalPreviewButton';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import PreviewDocument from '@/ui/components/PreviewQuoteInvoice/PreviewDocument';
import ResendModal from '@/ui/components/resendModal/ResendModal';
import { usePermissions } from '@/hooks/usePermissions';
import { downloadFile } from '@/utils/download-file';
import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';
import { Modal } from '@payaca/ambrosia';

import { getChangeProposalPLBadgeState } from '@payaca/helpers/changeProposalHelper';
import { getContactFirstName } from '@payaca/helpers/contactNameHelper';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { buildEmailSubjectPrefixForProjectFromProject } from '@payaca/helpers/dealHelper';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { ProposalsPermissions } from '@payaca/permissions/proposals/proposals.permissions';
import { requestGetCustomer } from '@payaca/store/customer/customerActions';
import { requestGetDeal } from '@payaca/store/deals/dealsActions';
import { requestGetJobContentsForDeal } from '@payaca/store/jobContent/jobContentActions';
import { requestGetJobsForDeal } from '@payaca/store/jobs/jobsActions';
import {
  getChangeProposalGeneratedPdfUrl,
  markChangeProposalAccepted,
  markChangeProposalDeclined,
  requestGetChangeProposal,
  requestGetChangeProposalsForDeal,
  sendChangeProposal,
  voidChangeProposal,
} from '@payaca/store/proposals/proposalsActions';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useHistory } from 'react-router';
import './ChangeProposalPage.sass';

type Props = {
  changeProposalId: number;
  projectId?: string;
};

const ChangeProposalPage: FC<Props> = ({
  changeProposalId,
  projectId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const changeProposal = useChangeProposal(changeProposalId);
  const deal = useDeal(changeProposal?.dealId);

  const attachmentUploads = useUploads(changeProposal?.attachmentUploadIds);

  const [isMarkingAsAccepted, setIsMarkingAsAccepted] = useState(false);
  const [isMarkingAsDeclined, setIsMarkingAsDeclined] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [isGettingPdfUrl, setIsGettingPdfUrl] = useState(false);
  const [
    showConfirmVoidChangeProposalModal,
    setShowConfirmVoidChangeProposalModal,
  ] = useState(false);
  const [showBounceResendModal, setShowBounceResendModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const { userHasRequiredPermission } = usePermissions();

  const companyName = useSelector(
    (state: any) => state.users.myProfile.accounts[0].companyName
  );

  const customer = useCustomerForDeal(changeProposal?.dealId);

  const customerContact = useMemo(() => {
    if (!customer) return;
    return getJobContactFromCustomer(
      customer,
      changeProposal?.contactId || null
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'customer'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: customer.contacts
  }, [customer?.contacts, changeProposal?.contactId]);

  const region = useRegion();

  const emailSubjectPrefix = deal
    ? buildEmailSubjectPrefixForProjectFromProject(deal)
    : undefined;

  const resendConfig = useMemo<
    ComponentProps<typeof ResendModal>['resendConfig'] | undefined
  >(() => {
    if (emailSubjectPrefix === undefined) return undefined;
    return {
      handleResend: async (requestData) => {
        dispatch(
          sendChangeProposal.request({
            isResend: true,
            changeProposalId: changeProposalId,
            emailCopy: requestData.emailCopy,
            // oxlint-disable-next-line typescript/no-misused-promises -- FIXME: pre-existing error: Promise-returning function provided to property where a void return was expected.
            callback: () => {
              dispatch(requestGetChangeProposal(changeProposalId));
              return Promise.resolve();
            },
            // oxlint-disable-next-line typescript/no-misused-promises -- FIXME: pre-existing error: Promise-returning function provided to property where a void return was expected.
            onErrorCallback: (errorMessages) => {
              return Promise.reject(errorMessages);
            },
          })
        );
      },
      documentType: 'change proposal',
      emailConfig: {
        subject: `${emailSubjectPrefix}Your change proposal from ${companyName}`,
        bodyTemplate: `
Dear [customer_name],
Your change proposal representing a value change of [value_change] can be viewed here:
[button]
Thanks,
[company_name]
              `,
        substitutions: [
          {
            find: 'reference',
            replaceWith:
              changeProposal?.customReference ||
              String(changeProposal?.reference),
          },
          { find: 'company_name', replaceWith: companyName },
          { find: 'customer_name', replaceWith: customerContact?.name },
          {
            find: 'customer_first_name',
            replaceWith: getContactFirstName(customerContact?.name),
          },
          {
            find: 'value_change',
            replaceWith: currencyPrice(
              changeProposal?.valueChangeIncTax || 0,
              region
            ),
          },
        ],
      },
    };
  }, [
    dispatch,
    changeProposalId,
    emailSubjectPrefix,
    companyName,
    changeProposal?.customReference,
    changeProposal?.reference,
    customerContact?.name,
    changeProposal?.valueChangeIncTax,
    region,
  ]);

  const fetchPdfRetry = useCallback(() => {
    return backOff(
      async () => {
        return new Promise<void>((resolve, reject) => {
          dispatch(
            requestGetChangeProposal(changeProposalId, (po) => {
              if (po.pdfUrl) {
                resolve();
              } else {
                reject();
              }
            })
          );
        });
      },
      {
        numOfAttempts: 5,
        startingDelay: 1500,
        delayFirstAttempt: true,
      }
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [changeProposalId]);

  const handleMarkAsAccepted = useCallback(() => {
    setIsMarkingAsAccepted(true);
    dispatch(
      markChangeProposalAccepted.request(
        changeProposalId,
        () => {
          setIsMarkingAsAccepted(false);
          dispatch(requestGetChangeProposal(changeProposalId));
          // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
          fetchPdfRetry();
        },
        (_err) => setIsMarkingAsAccepted(false)
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'fetchPdfRetry', and 'dispatch'
  }, [changeProposalId]);

  const handleMarkAsDeclined = useCallback(() => {
    setIsMarkingAsDeclined(true);
    dispatch(
      markChangeProposalDeclined.request(
        changeProposalId,
        () => {
          setIsMarkingAsDeclined(false);
          dispatch(requestGetChangeProposal(changeProposalId));
          // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
          fetchPdfRetry();
        },
        (_err) => setIsMarkingAsDeclined(false)
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [changeProposalId, fetchPdfRetry]);

  useEffect(() => {
    if (!changeProposal?.pdfUrl) {
      // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
      fetchPdfRetry();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'changeProposal.pdfUrl', and 'fetchPdfRetry'
  }, []);

  useEffect(() => {
    dispatch(requestGetChangeProposal(changeProposalId));
    dispatch(
      uploadsActions.requestGetUploadsForEntity(
        changeProposalId,
        'changeProposal'
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'changeProposalId', and 'dispatch'
  }, []);

  useEffect(() => {
    if (changeProposal?.dealId) {
      dispatch(requestGetDeal(changeProposal.dealId));
      dispatch(requestGetJobsForDeal(changeProposal.dealId));
      dispatch(requestGetJobContentsForDeal(changeProposal.dealId));
      dispatch(requestGetChangeProposalsForDeal(changeProposal.dealId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [changeProposal?.dealId]);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(requestGetCustomer(deal.customerId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal?.customerId]);

  useEffect(() => {
    if (changeProposal && !changeProposal?.sentAt) {
      history.push(
        `/projects/${projectId}/changeproposals/${changeProposalId}/edit`
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'history', 'changeProposalId', 'changeProposal', and 'projectId'
  }, [changeProposal?.sentAt]);

  const handleDownloadChangeProposal = useCallback(() => {
    if (!changeProposal) return;
    setIsGettingPdfUrl(true);
    dispatch(
      getChangeProposalGeneratedPdfUrl.request({
        changeProposalId: changeProposalId,
        callback: (pdfUrl: string) => {
          setIsGettingPdfUrl(false);
          downloadFile(
            pdfUrl,
            `change-proposal-${
              changeProposal.customReference || changeProposal.reference
            }.pdf`
          );
          requestGetChangeProposal(changeProposalId);
        },
        onErrorCallback: () => setIsGettingPdfUrl(false),
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [changeProposal, setIsGettingPdfUrl, changeProposalId]);

  const availableContextMenuItems = useMemo(() => {
    const actions: TDropdownItem[] = [];

    if (changeProposal?.voidedAt) {
      return actions;
    }

    if (
      !changeProposal?.acceptedAt &&
      !changeProposal?.declinedAt &&
      userHasRequiredPermission([
        ProposalsPermissions.MARK_CHANGE_PROPOSAL_ACCEPTED,
      ])
    ) {
      actions.push({
        label: 'Mark as accepted',
        onClick: handleMarkAsAccepted,
        isProcessing: isMarkingAsAccepted,
        disabled: isMarkingAsAccepted,
      });
    }

    if (
      !changeProposal?.acceptedAt &&
      !changeProposal?.declinedAt &&
      userHasRequiredPermission([
        ProposalsPermissions.MARK_CHANGE_PROPOSAL_DECLINED,
      ])
    ) {
      actions.push({
        label: 'Mark as declined',
        onClick: handleMarkAsDeclined,
        isProcessing: isMarkingAsDeclined,
        disabled: isMarkingAsDeclined,
      });
    }

    if (
      changeProposal?.sentAt &&
      !changeProposal?.bouncedAt &&
      userHasRequiredPermission([ProposalsPermissions.SEND_CHANGE_PROPOSAL])
    ) {
      actions.push({
        label: 'Resend',
        onClick: () => setShowResendModal(true),
      });
    }

    if (
      !changeProposal?.acceptedAt &&
      userHasRequiredPermission([ProposalsPermissions.VOID_CHANGE_PROPOSAL])
    ) {
      actions.push({
        label: 'Void this change proposal',
        onClick: () => setShowConfirmVoidChangeProposalModal(true),
      });
    }

    return actions;
  }, [
    changeProposal,
    userHasRequiredPermission,
    handleMarkAsAccepted,
    handleMarkAsDeclined,
    setShowResendModal,
    setShowConfirmVoidChangeProposalModal,
    isMarkingAsAccepted,
    isMarkingAsDeclined,
  ]);

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Back', to: `/projects/${projectId}/proposals` },
        { label: 'Change proposal' },
      ]}
    >
      <div className="p-4">
        <PreviewDocument
          title="Change proposal"
          badge={{
            variant: 'soft',
            colour: getChangeProposalPLBadgeState(
              changeProposal?.readableStatus
            ),
            children: changeProposal?.readableStatus,
          }}
          firstAction={
            changeProposal?.pdfUrl
              ? {
                  children: (
                    <UntitledIcon name="download-01.3" className="h-5 w-5" />
                  ),
                  onClick: () => {
                    // Todo: downloadable link for a context menu item?
                    window.open(changeProposal.pdfUrl, '_blank');
                  },
                }
              : {
                  children: (
                    <UntitledIcon name="download-01.3" className="h-5 w-5" />
                  ),
                  onClick: handleDownloadChangeProposal,
                  isProcessing: isGettingPdfUrl,
                  disabled: isGettingPdfUrl,
                }
          }
          actions={availableContextMenuItems}
          alerts={changeProposal?.warnings?.map((warning) => ({
            colour: 'yellow',
            iconName: 'alert-triangle.3',
            children: warning.message,
          }))}
          leadingAction={
            // Only a sent Change Proposal — there is nothing for the Customer
            // to have seen of one they were never sent.
            changeProposal?.sentAt && deal?.publicId ? (
              <ClientPortalPreviewButton projectId={deal.publicId} />
            ) : undefined
          }
        >
          {changeProposal?.bouncedAt && (
            <FeedbackBlock feedbackLevel={FeedbackLevel.ALERT}>
              <p>
                The email sending this change proposal to your customer has
                bounced
              </p>
              <PermissionGuard
                renderIfHasPermissions={[
                  ProposalsPermissions.SEND_CHANGE_PROPOSAL,
                ]}
              >
                <Button
                  variant="link"
                  onClick={() => setShowBounceResendModal(true)}
                >
                  Retry
                </Button>
              </PermissionGuard>
            </FeedbackBlock>
          )}

          <ChangeProposalDocument
            isPreview
            changeProposalId={changeProposalId}
          />
        </PreviewDocument>
      </div>
      <Modal
        isOpen={showConfirmVoidChangeProposalModal}
        onClose={() => setShowConfirmVoidChangeProposalModal(false)}
        title={'Are you sure you want to void the change proposal?'}
      >
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              colour="negative"
              onClick={() => {
                if (isVoiding) return;
                setIsVoiding(true);
                dispatch(
                  voidChangeProposal.request(
                    changeProposalId,
                    () => {
                      dispatch(requestGetChangeProposal(changeProposalId));
                      setIsVoiding(false);
                      setShowConfirmVoidChangeProposalModal(false);
                      // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
                      fetchPdfRetry();
                    },
                    (_err) => setIsVoiding(false)
                  )
                );
              }}
              isProcessing={isVoiding}
              replaceContentWhenProcessing
            >
              Void
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
      {deal?.customerId && resendConfig && (
        <BounceResendModal
          title="Resend change proposal"
          isOpen={showBounceResendModal}
          onClose={() => setShowBounceResendModal(false)}
          customerId={deal.customerId}
          customerContactId={changeProposal?.contactId}
          resendConfig={resendConfig}
        />
      )}
      {deal?.customerId && resendConfig && (
        <ResendModal
          title="Resend change proposal"
          isOpen={showResendModal}
          onClose={() => setShowResendModal(false)}
          customerId={deal.customerId}
          customerContactId={changeProposal?.contactId}
          resendConfig={resendConfig}
        />
      )}
    </AuthenticatedPageWrapper>
  );
};

export default ChangeProposalPage;
